import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Printer, Droplets, Building2, History, MapPin, FileSpreadsheet, AlertTriangle, Eye } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { SitesDialog } from "../printers/SitesDialog";
import { fmtMoney } from "../printers/constants";
import PrintersListView from "./PrintersList";
import PrinterConsumablesView from "./PrinterConsumables";
import PrinterCompaniesView from "./PrinterCompanies";
import PrinterJournalView from "./PrinterJournal";

const TABS = [
    { key: "printers", label: "Imprimantes", icon: Printer },
    { key: "consumables", label: "Consommables", icon: Droplets },
    { key: "companies", label: "Entreprises", icon: Building2 },
    { key: "journal", label: "Journal", icon: History },
];

function Stat({ value, label, tone = "gray", onClick, testId, alert }) {
    const tones = {
        gray: "bg-gray-50 text-gray-800", teal: "bg-teal-50 text-teal-700", red: "bg-red-50 text-red-700",
        orange: "bg-orange-50 text-orange-700", emerald: "bg-emerald-50 text-emerald-700", blue: "bg-blue-50 text-blue-700",
    };
    return (
        <button onClick={onClick} disabled={!onClick} data-testid={testId}
            className={`px-3 py-2.5 rounded-lg text-center transition-shadow ${tones[tone]} ${onClick ? "hover:shadow-md cursor-pointer" : "cursor-default"}`}>
            <p className="text-lg font-bold flex items-center justify-center gap-1">{alert && <AlertTriangle className="w-3.5 h-3.5" />}{value}</p>
            <p className="text-[10px] opacity-80">{label}</p>
        </button>
    );
}

export default function Printers() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasPermission } = useApp();
    const canManage = hasPermission("printers:manage");
    const tab = searchParams.get("tab") || "printers";
    const [sites, setSites] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [stats, setStats] = useState(null);
    const [sitesOpen, setSitesOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const loadShared = useCallback(async () => {
        try {
            const [s, c, p, st] = await Promise.all([api.getPrinterSites(), api.getPrinterCompanies(), api.getPrinters(), api.getPrinterStats()]);
            setSites(s);
            setCompanies(c);
            setPrinters(p.printers);
            setStats(st);
        } catch { toast.error("Erreur de chargement"); }
    }, []);

    useEffect(() => { loadShared(); }, [loadShared, refreshKey]);

    const onChanged = () => setRefreshKey((k) => k + 1);
    const goTab = (key, extra = {}) => setSearchParams({ tab: key, ...extra });

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await api.exportPrintersExcel();
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `imprimantes_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch { toast.error("Erreur export"); }
        finally { setExporting(false); }
    };

    const shared = { sites, companies, printers, onChanged, refreshKey, searchParams, goTab };
    const problems = (stats?.by_status?.broken || 0) + (stats?.by_status?.maintenance || 0);

    return (
        <div className="page-container" data-testid="printers-page">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Printer className="w-5 h-5 text-teal-600" />
                <h1 className="text-lg font-bold text-gray-900">Imprimantes & Consommables</h1>
                {!canManage && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600" data-testid="printers-readonly-badge"><Eye className="w-3 h-3" />Lecture seule</span>}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-2">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => goTab(t.key)} data-testid={`ptab-${t.key}`}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === t.key ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                            <t.icon className="w-3.5 h-3.5" />{t.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    {canManage && <Button variant="outline" size="sm" onClick={() => setSitesOpen(true)} data-testid="printer-sites-btn"><MapPin className="w-4 h-4 mr-1" />Sites</Button>}
                    <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-printers-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />{exporting ? "..." : "Exporter"}</Button>
                </div>
            </div>

            {stats && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5" data-testid="printer-stats">
                    <Stat value={stats.printers_total} label="Imprimantes" tone="gray" onClick={() => goTab("printers")} testId="stat-printers" />
                    <Stat value={problems} label="Panne / maintenance" tone={problems ? "red" : "emerald"} alert={problems > 0} onClick={() => goTab("printers", { status: "broken" })} testId="stat-problems" />
                    <Stat value={stats.checks_overdue} label={`Contrôles > ${stats.check_overdue_days} j`} tone={stats.checks_overdue ? "orange" : "emerald"} onClick={() => goTab("printers", { overdue: "1" })} testId="stat-checks-overdue" />
                    <Stat value={stats.consumables_total} label="Consommables" tone="blue" onClick={() => goTab("consumables")} testId="stat-consumables" />
                    <Stat value={stats.low_stock} label={`Stock bas${stats.out_of_stock ? ` (${stats.out_of_stock} rupture)` : ""}`} tone={stats.low_stock ? "orange" : "emerald"} alert={stats.low_stock > 0} onClick={() => goTab("consumables", { low: "1" })} testId="stat-low-stock" />
                    <Stat value={fmtMoney(stats.stock_value)} label="Valeur du stock" tone="teal" testId="stat-stock-value" />
                </div>
            )}

            {tab === "printers" && <PrintersListView {...shared} />}
            {tab === "consumables" && <PrinterConsumablesView {...shared} />}
            {tab === "companies" && <PrinterCompaniesView {...shared} />}
            {tab === "journal" && <PrinterJournalView {...shared} />}

            <SitesDialog open={sitesOpen} onClose={() => setSitesOpen(false)} sites={sites} onChanged={onChanged} />
        </div>
    );
}
